import { useApolloClient } from '@apollo/client'
import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseBestPracticeDetailsState {
    data: BestPracticeData | null
    loading: boolean
    error: boolean
}

interface UseBestPracticeDetailsReturn extends UseBestPracticeDetailsState {
    /** Triggers the fetch (idempotent — no-op once data or error is recorded). */
    fetch: () => void
    /** Clears state so the next fetch() call will re-issue the query. */
    reset: () => void
}

interface UseBestPracticeDetailsOptions {
    /** When true, automatically fires fetch once on mount. */
    fetchOnMount?: boolean
}

const initialState: UseBestPracticeDetailsState = {
    data: null,
    loading: false,
    error: false,
}

export const useBestPracticeDetails = (
    contentItemId: string | undefined,
    projectId: string | undefined,
    { fetchOnMount = false }: UseBestPracticeDetailsOptions = {}
): UseBestPracticeDetailsReturn => {
    const apolloClient = useApolloClient()
    const [state, setState] = useState<UseBestPracticeDetailsState>(
        initialState
    )
    // Latest state mirrored into a ref so `fetch`'s identity stays stable
    // across re-renders without listing `state` as a dependency.
    const stateRef = useRef(state)
    stateRef.current = state

    const fetch = useCallback(() => {
        if (!contentItemId || !projectId) return
        const s = stateRef.current
        if (s.data || s.loading || s.error) return

        setState({ data: null, loading: true, error: false })
        apolloClient
            .query<{ getBestPractice: BestPracticeData | null }>({
                query: BestPracticeQuery,
                variables: { parentId: contentItemId, projectId },
                fetchPolicy: 'no-cache',
            })
            .then(({ data }) => {
                setState({
                    data: data?.getBestPractice ?? null,
                    loading: false,
                    error: false,
                })
            })
            .catch(() => {
                setState({ data: null, loading: false, error: true })
            })
    }, [apolloClient, contentItemId, projectId])

    const reset = useCallback(() => setState(initialState), [])

    useEffect(() => {
        if (fetchOnMount) fetch()
    }, [fetchOnMount, fetch])

    return { ...state, fetch, reset }
}
